import React, { useState, useRef, useCallback } from 'react';
import { messagesAPI } from '../api';
import { Upload, Download, Send, CheckCircle2, XCircle, Loader2, FileSpreadsheet, Info } from 'lucide-react';
import * as XLSX from 'xlsx';

// This tab handles Excel where each row has all subjects as columns
// Auto-computes percentage and percentile rank

export default function AllSubjectMarksSender({ sessionId, schoolName }) {
  const [file, setFile] = useState(null);
  const [students, setStudents] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [sendMode, setSendMode] = useState('text'); // eslint-disable-line no-unused-vars
  const fileInputRef = useRef(null);

  const parseAllSubjectsExcel = useCallback((buffer) => {
    try {
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (rows.length === 0) return { students: [], headers: [] };

      const firstRow = rows[0];
      const allKeys = Object.keys(firstRow);
      const fixedCols = ['Name', 'Mobile Number', 'name', 'mobile_number', 'Phone'];
      const subjectCols = allKeys.filter(k => !fixedCols.some(f => f.toLowerCase() === k.toLowerCase()));

      const parsed = rows.map((row) => {
        const name = String(row['Name'] || row['name'] || '').trim();
        const phone = String(row['Mobile Number'] || row['mobile_number'] || row['Phone'] || '').trim();

        const subjectMarks = {};
        let totalObtained = 0;
        let totalMax = 0;

        subjectCols.forEach((col) => {
          const val = row[col];
          // Each cell can be "obtained/total" or just a number
          if (typeof val === 'string' && val.includes('/')) {
            const [obtained, total] = val.split('/').map(Number);
            subjectMarks[col] = { obtained: obtained || 0, total: total || 100 };
            totalObtained += obtained || 0;
            totalMax += total || 100;
          } else {
            const obtained = Number(val) || 0;
            subjectMarks[col] = { obtained, total: 100 };
            totalObtained += obtained;
            totalMax += 100;
          }
        });

        const percentage = totalMax > 0 ? ((totalObtained / totalMax) * 100).toFixed(1) : '0';

        return { name, phone, subjectMarks, totalObtained, totalMax, percentage: parseFloat(percentage), subjectCols };
      });

      // Compute percentile rank for each student
      const percentages = parsed.map(s => s.percentage);
      const withPercentile = parsed.map((student) => {
        const below = percentages.filter(p => p < student.percentage).length;
        const percentile = ((below / parsed.length) * 100).toFixed(0);
        return { ...student, percentileRank: parseInt(percentile) };
      });

      return { students: withPercentile, headers: subjectCols };
    } catch (err) {
      return { students: [], headers: [], error: err.message };
    }
  }, []);

  const handleFileSelect = useCallback((selectedFile) => {
    if (!selectedFile) return;
    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      setError('Please upload a valid Excel file');
      return;
    }
    setFile(selectedFile);
    setError('');
    setResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const { students: parsed, headers: hdrs, error: parseError } = parseAllSubjectsExcel(e.target.result);
      if (parseError) { setError('Failed to parse file: ' + parseError); return; }
      setStudents(parsed);
      setHeaders(hdrs);
    };
    reader.readAsArrayBuffer(selectedFile);
  }, [parseAllSubjectsExcel]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      { 'Name': 'John Doe', 'Mobile Number': '919876543210', 'Mathematics': '85/100', 'Science': '78/100', 'English': '90/100', 'Hindi': '72/100', 'Social Studies': '80/100' },
      { 'Name': 'Jane Smith', 'Mobile Number': '919876543211', 'Mathematics': '92/100', 'Science': '88/100', 'English': '76/100', 'Hindi': '84/100', 'Social Studies': '91/100' },
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = [{ wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, ws, 'All Subjects Template');
    const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'all_subjects_template.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSend = async () => {
    if (!file || students.length === 0) { setError('Please upload an Excel file first'); return; }
    setSending(true);
    setError('');
    setResult(null);

    const results = [];
    let sent = 0, failed = 0;

    for (const student of students) {
      if (!student.phone) {
        results.push({ name: student.name, status: 'skipped', reason: 'No phone' });
        continue;
      }

      // Build subject-by-subject summary
      const subjectLines = student.subjectCols.map((sub) => {
        const m = student.subjectMarks[sub];
        const pct = m.total > 0 ? ((m.obtained / m.total) * 100).toFixed(0) : 0;
        return `  ${sub}: ${m.obtained}/${m.total} (${pct}%)`;
      }).join('\n');

      const remark = student.percentage >= 90 ? 'Outstanding' :
        student.percentage >= 75 ? 'Excellent' :
        student.percentage >= 60 ? 'Good' :
        student.percentage >= 45 ? 'Average' :
        student.percentage >= 33 ? 'Pass' : 'Fail';

      const message = `Dear Parent,
Student: ${student.name}
School: ${schoolName || 'School'}

Subject-wise Marks:
${subjectLines}

Total: ${student.totalObtained}/${student.totalMax}
Overall Percentage: ${student.percentage}%
Percentile Rank: ${student.percentileRank}th percentile
Remark: ${remark}

Thank you`;

      const formData = new FormData();
      formData.append('sessionId', sessionId);
      formData.append('phone', student.phone);
      formData.append('message', message);

      try {
        const res = await messagesAPI.sendManual({ sessionId, phone: student.phone, message });
        results.push({ name: student.name, status: 'sent' });
        sent++;
      } catch (err) {
        results.push({ name: student.name, status: 'failed', reason: err.response?.data?.error || err.message });
        failed++;
      }

      await new Promise(r => setTimeout(r, 800));
    }

    setResult({ total: students.length, sent, failed, results });
    setSending(false);
  };

  const getRemark = (pct) => {
    if (pct >= 90) return { label: 'Outstanding', color: 'text-purple-600 bg-purple-50' };
    if (pct >= 75) return { label: 'Excellent', color: 'text-green-600 bg-green-50' };
    if (pct >= 60) return { label: 'Good', color: 'text-blue-600 bg-blue-50' };
    if (pct >= 45) return { label: 'Average', color: 'text-yellow-600 bg-yellow-50' };
    if (pct >= 33) return { label: 'Pass', color: 'text-orange-600 bg-orange-50' };
    return { label: 'Fail', color: 'text-red-600 bg-red-50' };
  };

  return (
    <div className="space-y-5">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>All Subjects Mode:</strong> Each row is a student with all subjects as columns.
          Format marks as <code className="bg-blue-100 px-1 rounded">85/100</code> or just <code className="bg-blue-100 px-1 rounded">85</code> (assumes total 100).
          Percentage and Percentile Rank are auto-computed.
        </div>
      </div>

      {/* Upload */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <FileSpreadsheet size={18} className="text-blue-600" />
          Upload All-Subjects Excel
        </h3>

        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            dragActive ? 'border-blue-500 bg-blue-50' :
            file ? 'border-green-400 bg-green-50' :
            'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
          }`}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onClick={() => fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden"
            onChange={(e) => handleFileSelect(e.target.files[0])} />
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <CheckCircle2 size={36} className="text-green-500" />
              <p className="font-semibold text-green-700">{file.name}</p>
              <p className="text-sm text-green-600">{students.length} students, {headers.length} subjects</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <Upload size={36} className="text-gray-400" />
              <p className="font-semibold text-gray-600">Drag & drop Excel with all subjects</p>
              <button type="button" className="btn-3d-blue text-sm py-2 px-4"
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                Choose File
              </button>
            </div>
          )}
        </div>

        <div className="mt-3">
          <button onClick={handleDownloadTemplate} className="btn-3d-blue text-sm py-2 px-4 flex items-center gap-2">
            <Download size={14} />
            Download Template
          </button>
        </div>
      </div>

      {/* Preview Table */}
      {students.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <span className="font-semibold text-gray-700 text-sm">Preview</span>
            <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-medium">
              {students.length} students
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">#</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">Name</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">Phone</th>
                  {headers.map(h => (
                    <th key={h} className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">Total %</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">Percentile</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">Remark</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, i) => {
                  const { label, color } = getRemark(student.percentage);
                  return (
                    <tr key={i} className={`border-b border-gray-100 hover:bg-blue-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                      <td className="px-3 py-2 text-xs text-gray-400">{i + 1}</td>
                      <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{student.name}</td>
                      <td className="px-3 py-2 text-gray-600 font-mono text-xs">{student.phone}</td>
                      {headers.map(h => (
                        <td key={h} className="px-3 py-2 text-center text-gray-700 whitespace-nowrap">
                          {student.subjectMarks[h]?.obtained}/{student.subjectMarks[h]?.total}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-center">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${student.percentage >= 75 ? 'bg-green-50 text-green-700' : student.percentage >= 45 ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-600'}`}>
                          {student.percentage}%
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center text-xs font-semibold text-blue-700">{student.percentileRank}th</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${color}`}>{label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <XCircle size={18} className="text-red-500 flex-shrink-0" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {result && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={18} className="text-green-600" />
            <span className="font-semibold text-green-700">Done! Sent {result.sent}/{result.total} messages</span>
          </div>
        </div>
      )}

      <button
        onClick={handleSend}
        disabled={sending || students.length === 0}
        className={`w-full py-4 rounded-xl font-bold text-white text-lg flex items-center justify-center gap-3 transition-all transform
          ${sending || students.length === 0
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-orange-500 hover:bg-orange-600 shadow-[0_6px_0_#c2410c] active:shadow-[0_2px_0_#c2410c] active:translate-y-1'
          }`}
      >
        {sending ? <><Loader2 size={22} className="animate-spin" />Sending...</> : <><Send size={22} />Send to All Students ({students.length})</>}
      </button>
    </div>
  );
}
